/**
 * McpSessionWarning — nicht-blockierender Hinweis-Banner im Briefing-Tab,
 * wenn die MCP-Verbindung nicht aktiv ist.
 *
 * Rendert nichts, wenn:
 *   - Status === 'connected' (alles ok)
 *   - Anfrage liefert 503 / schlägt fehl (MCP nicht konfiguriert)
 *   - Anfrage lädt noch
 *
 * Zustand 'unavailable' (Orakel-Stoerung, PV-045): reine Info, KEINE
 * Login-Aufforderung — ein Login-Fenster darf sich nie ungefragt oeffnen,
 * und bei einer Stoerung wuerde ein erneuter Login ohnehin nichts loesen.
 * 'revoked'/'none' verlangen dagegen echten Neu-Login ueber „Jetzt verbinden".
 *
 * Der Generate-Button wird durch diesen Banner NICHT beeinflusst.
 */
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { ApiError, api } from '../../lib/api.js';

export function McpSessionWarning() {
  const { t } = useTranslation();
  const location = useLocation();

  const statusQuery = useQuery({
    queryKey: ['mcp-status'],
    queryFn: () => api.getMcpStatus(),
    // 503 = MCP nicht konfiguriert → kein Banner, kein Retry
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 503) return false;
      return failureCount < 2;
    },
    // Fehler nicht im Error-Boundary hochgeben — Banner bleibt einfach weg
    throwOnError: false,
  });

  // Noch ladend, fehlgeschlagen (inkl. 503), oder bereits connected → nichts rendern
  if (statusQuery.isLoading || statusQuery.isError) return null;
  const status = statusQuery.data?.status;
  if (!status || status === 'connected') return null;

  // Leicht unterschiedliche Formulierung je nach Zustand
  const isUnavailable = status === 'unavailable';
  const mainText = isUnavailable
    ? t(
        'v2.detail.mcp_warning_unavailable',
        'Orakel gerade nicht erreichbar — Briefings werden ohne Orakel-Quellen erzeugt, es wird automatisch erneut versucht.',
      )
    : status === 'revoked'
      ? t(
          'v2.detail.mcp_warning_revoked',
          'MCP-Zugang widerrufen — Briefings werden ohne MCP-Quellen erzeugt.',
        )
      : t(
          'v2.detail.mcp_warning_none',
          'MCP-Quellen nicht einbezogen — bitte MCP-Login herstellen.',
        );

  return (
    <div
      role="alert"
      className="flex items-start gap-3 rounded-lg px-4 py-3 text-sm"
      style={{
        background: 'var(--warn-soft)',
        border: '1px solid var(--warn)',
        color: 'var(--ink)',
      }}
    >
      {/* Warn-Icon */}
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        className="shrink-0 mt-0.5"
        style={{ color: 'var(--warn)' }}
      >
        <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>

      <div className="flex-1 min-w-0">
        <span>{mainText}</span> {/* Bei Stoerung hilft kein Login — keine Aufforderung (PV-045). */}
        {!isUnavailable && (
          <>
            {/* PV-046: direkt verbinden und danach genau hierher zurueck. */}
            <a
              href={api.getMcpConnectUrl(location.pathname + location.search)}
              className="underline font-medium mr-3"
              style={{ color: 'var(--warn)' }}
            >
              {t('v2.detail.mcp_warning_connect_link', 'Jetzt verbinden →')}
            </a>
            <Link
              to="/einstellungen"
              className="underline font-medium"
              style={{ color: 'var(--warn)' }}
            >
              {t('v2.detail.mcp_warning_settings_link', 'Einstellungen öffnen →')}
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
